/**
 * KYC (Didit) — helpers for `GET/POST/DELETE /api/v1/kyc/*` (canonical SessionPayload).
 * Legacy `GET /v1/user/kyc-status` uses different `data.status` strings; use `toCanonicalStatus`.
 */

const CANONICAL = new Set(["NOT_STARTED", "PENDING", "APPROVED", "REJECTED", "EXPIRED", "RESUBMISSION_REQUESTED"]);

const ALIAS_APPROVED = new Set(["VERIFIED", "SUCCESS", "COMPLETE"]);
const ALIAS_PENDING = new Set([
  "IN_PROGRESS",
  "SUBMITTED",
  "PROCESSING",
  "REVIEW",
  "IN_REVIEW",
  "AWAITING",
  "INITIATED",
  "STARTED",
  "RESUBMITTED",
]);
const ALIAS_REJECTED = new Set(["DECLINED", "FAILED", "CANCELLED"]);

/** Legacy wire → canonical (DIDIT_KYC_BACKEND / internal mapping). */
const LEGACY_STATUS_TO_CANONICAL = {
  draft: "NOT_STARTED",
  submitted: "PENDING",
  under_review: "PENDING",
  approved: "APPROVED",
  rejected: "REJECTED",
  expired: "EXPIRED",
};

export function toCanonicalStatus(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (!s) return "";
  const low = s.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(LEGACY_STATUS_TO_CANONICAL, low)) {
    return LEGACY_STATUS_TO_CANONICAL[low];
  }
  const up = s.toUpperCase().replace(/-/g, "_");
  if (CANONICAL.has(up)) return up;
  if (ALIAS_APPROVED.has(up)) return "APPROVED";
  if (ALIAS_PENDING.has(up)) return "PENDING";
  if (ALIAS_REJECTED.has(up)) return "REJECTED";
  return up;
}

/** `success` + `data` from `ApiCallGet` / `ApiCallPost` (SessionPayload at `data` or `data.data`). */
export function unwrapSessionPayload(result) {
  if (result == null || result.success === false) return null;
  const d = result.data;
  if (d == null) return null;
  if (typeof d === "object" && d.data != null && typeof d.data === "object" && !Array.isArray(d.data)) {
    return d.data;
  }
  return d;
}

function unwrapKycPayloadData(data) {
  if (data == null || typeof data !== "object") return null;
  if (data.data != null && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  return data;
}

/**
 * User tier 0–5 from SessionPayload. Prefer numeric `kycVerified`.
 * `PENDING` is both in-progress and under review — split with `diditStatus` / `diditVendorStatus`.
 */
export function getKycTierFromSessionPayload(p) {
  if (!p || typeof p !== "object") return null;
  const raw = p.kycVerified ?? p.kyc_verified;
  const c = toCanonicalStatus(p.status);
  if (raw !== undefined && raw !== null && raw !== "") {
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 1 && n <= 5) return n;
    // 0 on the session payload is often stale while Didit is already PENDING.
    if (n === 0 && (!c || c === "NOT_STARTED" || c === "EXPIRED")) return 0;
  }
  if (c === "APPROVED") return 2;
  if (c === "REJECTED") return 3;
  if (c === "RESUBMISSION_REQUESTED") return 5;
  if (c === "PENDING") {
    const didit = p.diditStatus ?? p.diditVendorStatus ?? p.didit_status;
    const s = String(didit || "").toLowerCase();
    if (s.includes("review")) return 4;
    return 1;
  }
  if (c === "NOT_STARTED" || c === "EXPIRED") return 0;
  return null;
}

/** Body for `POST /api/v1/kyc/session` (Didit). TEST: INDIA (Aadhaar / PAN). Flip back to DUBAI / AE for production UAE. */
export function buildKycSessionBody(userDetails) {
  const email = userDetails?.emailId || userDetails?.email;
  const cc = userDetails?.country_code || userDetails?.countryCode;
  const mobile = userDetails?.mobileNumber || userDetails?.phoneNumber || userDetails?.phone;
  const phone = mobile
    ? `${cc ? String(cc).replace(/\s/g, "") : ""}${String(mobile).replace(/\s/g, "")}`
    : undefined;
  const first = userDetails?.firstName || userDetails?.first_name;
  const last = userDetails?.lastName || userDetails?.last_name;

  const body = {
    jurisdiction: "INDIA",
    country_code: "IN",
    ...(email ? { email: String(email) } : {}),
    ...(phone && phone.length > 4 ? { phone } : {}),
    ...(first ? { firstName: String(first) } : {}),
    ...(last ? { lastName: String(last) } : {}),
  };
  if (typeof window !== "undefined" && window.location?.origin) {
    // Didit opens this URL after the user completes the session (must match DIDIT_CALLBACK_URL).
    body.returnUrl = `${window.location.origin}/kyc-submitted`;
  }
  if (typeof navigator !== "undefined" && !isLikelyHandheldPhoneUserAgent()) {
    // Hint for backend / Didit session creation: prefer in-browser (webcam) on PC — strip if your API returns 400.
    body.clientIntegration = "didit-sdk-web";
    body.preferInBrowserVerification = true;
  }
  return body;
}

/** Body for `POST /api/v1/kyb/session` (Didit). */
export function buildKybSessionBody(userDetails) {
  const body = buildKycSessionBody(userDetails);
  body.jurisdiction = "GLOBAL";
  if (typeof window !== "undefined" && window.location?.origin) {
    body.returnUrl = `${window.location.origin}/user_profile/kyb`;
  }
  return body;
}

function isLikelyHandheldPhoneUserAgent() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPod|Android.*Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}

/**
 * Fields from `POST /api/v1/kyc/session` or `GET /api/v1/kyc/status` (SessionPayload).
 * If `inBrowserVerification` is missing, `inBrowserAvailable` defaults true (older responses).
 */
export function pickKycSessionFields(result) {
  if (result == null || result.success === false || result.data == null) {
    return { diditUrl: null, sessionToken: null, kycMode: null, inBrowserAvailable: true, statusCanonical: null, session: null };
  }
  const session = unwrapKycPayloadData(result.data) || result.data;
  const rawUrl =
    session?.diditUrl ??
    session?.didit_url ??
    session?.url ??
    session?.verificationUrl ??
    session?.verification_url ??
    session?.link ??
    session?.sessionUrl ??
    session?.session_url ??
    result?.diditUrl ??
    result?.url;
  const diditUrl = typeof rawUrl === "string" && rawUrl.length > 0 ? rawUrl : null;
  const st = session?.sessionToken ?? session?.session_token ?? session?.token ?? null;
  const sessionToken = typeof st === "string" && st.length > 0 ? st : null;
  const km = session?.kycMode ?? session?.kyc_mode ?? null;
  const kycMode =
    km === "EMBEDDED_AVAILABLE" || km === "REDIRECT_ONLY"
      ? km
      : typeof km === "string" && km.length > 0
        ? km
        : null;
  const inv = session?.inBrowserVerification;
  let inBrowserAvailable = true;
  if (inv != null && typeof inv === "object") {
    inBrowserAvailable = Object.prototype.hasOwnProperty.call(inv, "available")
      ? inv.available === true
      : true;
  }
  const statusCanonical = session?.status != null ? toCanonicalStatus(session.status) : null;
  return { diditUrl, sessionToken, kycMode, inBrowserAvailable, statusCanonical, session };
}

/** Heuristic: real phone in portrait-like use (optional analytics). */
export function isLikelyHandheldPhone() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  try {
    if (window.matchMedia("(max-width: 767px) and (pointer: coarse)").matches) return true;
  } catch {
    /* ignore */
  }
  return /iPhone|iPod|Android.*Mobile/i.test(navigator.userAgent || "");
}

/**
 * Always use @didit-protocol/sdk-web for `diditUrl` while KYC is active — **ignore** `inBrowserVerification.available` here.
 * That flag is about server policy; the same `diditUrl` in a new tab (QR) is worse for laptops than the SDK iframe.
 * `window.open` is only a fallback in `startDiditKycFlow` if the SDK throws.
 */
export function shouldUseDiditWebSdk({ statusCanonical, diditUrl, inBrowserAvailable: _inBrowserAvailable }) {
  if (!diditUrl) return false;
  return (
    !statusCanonical ||
    statusCanonical === "NOT_STARTED" ||
    statusCanonical === "PENDING" ||
    statusCanonical === "RESUBMISSION_REQUESTED"
  );
}

export function pickDiditUrlFromSessionResult(result) {
  return pickKycSessionFields(result).diditUrl;
}
